import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ApprovalActionPage = () => {
    const [searchParams] = useSearchParams();
    const [comment, setComment] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('READY'); 
    const [approvalPath, setApprovalPath] = useState([]);
    const [loadingPath, setLoadingPath] = useState(true);

    const action = searchParams.get('action'); 
    const tableCd = searchParams.get('tableCd');
    const docuCd = searchParams.get('docuCd');
    const docuNo = searchParams.get('docuNo');
    const sabun = searchParams.get('sabun');

    // Helper: Format Time
    const formatTime = (timeStr) => {
        if (!timeStr || timeStr.trim() === '') return '';
        const t = timeStr.trim().padStart(4, '0');
        return `${t.substring(0, 2)}:${t.substring(2, 4)}`;
    };

    useEffect(() => {
        const fetchPath = async () => {
            setLoadingPath(true);
            try {
                // Use the new PUBLIC status API
                const response = await fetch(`${API_BASE_URL}/approvals/status?tableCd=${tableCd}&docuCd=${docuCd}&docuNo=${docuNo}`);
                if (response.ok) {
                    const data = await response.json();
                    setApprovalPath(data || []);
                }
            } catch (err) { 
                console.error("Fetch Path Error:", err); 
            } finally { 
                setLoadingPath(false); 
            }
        };
        if (tableCd && docuCd) fetchPath();
    }, [tableCd, docuCd, docuNo]);

    // Check if document is already fully processed (rejected or approved)
    const docuSt = approvalPath.length > 0 ? (approvalPath[0].DOCU_ST || '').trim() : '';
    const isDocumentDone = docuSt === '0' || docuSt === '9';
    // Check if current user still has a pending step (supports same person appearing multiple times)
    const myPendingStep = approvalPath.find(step => step.KSABUN === sabun?.trim() && step.KSTAT !== '9' && step.KSTAT !== '0');
    const isAlreadyDone = isDocumentDone || (!myPendingStep && approvalPath.some(step => step.KSABUN === sabun?.trim()));
    const tableNm = approvalPath.length > 0 ? (approvalPath[0].TABLE_NM || '').trim() : '';
    const docuNm = approvalPath.length > 0 ? (approvalPath[0].DOCU_NM || '').trim() : '';
    const drafterStep = approvalPath.find(s => parseInt(s.DOCU_SER) === 11);
    const rawBigo = drafterStep ? (drafterStep.BIGO || '').trim() : '';
    let detailUrl = '', screenshotUrl = '';
    if (rawBigo.startsWith('{')) {
        try {
            const parsed = JSON.parse(rawBigo);
            detailUrl = parsed.detailUrl || '';
            screenshotUrl = parsed.screenshotUrl || '';
        } catch (e) { detailUrl = rawBigo; }
    } else {
        detailUrl = rawBigo;
    }

    const handleAction = async () => {
        if (isAlreadyDone) return;
        // 기각 시 의견 필수
        if (action !== 'approve' && (!comment || !comment.trim())) {
            alert('Please provide a reason for rejection.');
            return;
        }
        setIsProcessing(true);
        try {
            const endpoint = action === 'approve' ? 'approve' : 'reject';
            const response = await fetch(`${API_BASE_URL}/approvals/external/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tableCd, docuCd, docuNo, sabun, comment })
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Failed to process approval.');
            }
            setStatus('SUCCESS');
            // Notify WinForms WebView2 host (ERP 직접 결재 연동)
            try { if (window.chrome?.webview) window.chrome.webview.postMessage('APPROVAL_SUCCESS'); } catch (_) {}
        } catch (err) { alert(err.message); setStatus('ERROR'); } finally { setIsProcessing(false); }
    };

    if (status === 'SUCCESS') {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
                <div className="text-center max-w-sm">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-lg shadow-slate-200/50 animate-[bounce_1s_ease-in-out_2]">
                        <svg className="w-10 h-10 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">Decision Recorded</h3>
                    <p className="text-sm text-slate-400 mb-8">Your {action === 'approve' ? 'approval' : 'rejection'} has been processed successfully.</p>
                    <button onClick={() => window.close()} className="px-8 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-lg shadow-indigo-200/50 transition-all active:scale-95">
                        Close
                    </button>
                </div>
            </div>
        );
    }

    // 이미 결재 완료된 경우 메시지 화면 표시
    if (!loadingPath && isAlreadyDone) {
        const myDoneStep = approvalPath.find(step => step.KSABUN === sabun?.trim() && (step.KSTAT === '9' || step.KSTAT === '0'));
        const wasApproved = myDoneStep?.KSTAT === '9';
        const isDrafter = myDoneStep ? parseInt(myDoneStep.DOCU_SER) === 11 : false;
        const doneDate = myDoneStep?.KDATE ? myDoneStep.KDATE.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3') : '';
        const doneTime = formatTime(myDoneStep?.KTIME || '');

        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
                <div className="text-center max-w-sm">
                    <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-lg shadow-slate-200/50`}>
                        {wasApproved ? (
                            <svg className="w-10 h-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                        ) : (
                            <svg className="w-10 h-10 text-[#8b7572]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                        )}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">이미 결재되었습니다</h3>
                    <p className="text-sm text-slate-500 mb-1">
                        {isDrafter ? '제출' : wasApproved ? '승인' : '기각'} 처리된 문서입니다.
                    </p>
                    {docuNo && (
                        <p className="text-xs text-slate-400 mb-2">문서번호: {docuNo}</p>
                    )}
                    {doneDate && (
                        <p className="text-xs text-slate-400 mb-6">처리일시: {doneDate} {doneTime}</p>
                    )}
                    <div className="flex flex-col gap-3">
                        {screenshotUrl && (
                            <div>
                                <a href={screenshotUrl} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-all">
                                    📸 Screenshot (Web/Mobile)
                                </a>
                                <p className="text-[10px] text-slate-400 mt-1">* Static image only. Buttons and interactive features are not available.</p>
                            </div>
                        )}
                        {detailUrl && (
                            <div>
                                <a href={detailUrl} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 transition-all">
                                    📄 Open in ERP (Windows)
                                </a>
                                <p className="text-[10px] text-slate-400 mt-1">* ERP requires Windows PC with application installed.</p>
                            </div>
                        )}
                        <button onClick={() => window.close()} className="px-8 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-lg shadow-indigo-200/50 transition-all active:scale-95">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const isApprove = action === 'approve';

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30 py-10 px-4 font-sans">
            <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-6 items-start">

                {/* Left: Action Card */}
                <div className="flex-1 w-full">
                    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 overflow-hidden border border-slate-200/80">
                        {/* Header */}
                        <div className={`px-8 py-7 bg-gradient-to-r ${isApprove ? 'from-indigo-500 to-blue-600' : 'from-[#78716c] to-[#8b7572]'}`}>
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                                    {isApprove ? (
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    ) : (
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                    )}
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">{isApprove ? 'Approve Document' : 'Reject Document'}</h2>
                                    <p className="text-white/60 text-xs font-medium mt-0.5">{tableNm ? `[${tableNm}] ` : ''}{docuNm || docuCd}</p>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-8">
                            {/* View Document */}
                            <div className="mb-6 text-center space-y-3">
                                {screenshotUrl && (
                                    <div>
                                        <a href={screenshotUrl} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-all">
                                            📸 Screenshot (Web/Mobile)
                                        </a>
                                        <p className="text-[10px] text-slate-400 mt-1">* Static image only. Buttons and interactive features are not available.</p>
                                    </div>
                                )}
                                {detailUrl && (
                                    <div>
                                        <a href={detailUrl} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 transition-all">
                                            📄 Open in ERP (Windows)
                                        </a>
                                        <p className="text-[10px] text-slate-400 mt-1">* ERP requires Windows PC with application installed.</p>
                                    </div>
                                )}
                                {!screenshotUrl && !detailUrl && (
                                    <p className="text-[11px] text-slate-400 bg-slate-50 rounded-lg py-3 px-4">
                                        <svg className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                        To view document details, please open the ERP application on your Windows PC.
                                    </p>
                                )}
                            </div>

                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em] mb-3">Comment (Optional)</p>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Add your feedback or reason..."
                                className="w-full border border-slate-200 rounded-xl p-4 bg-slate-50/50 focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none transition-all resize-none min-h-[140px] text-sm text-slate-700"
                            />

                            <button
                                onClick={handleAction}
                                disabled={isProcessing || isAlreadyDone}
                                className={`w-full mt-6 py-4 rounded-xl font-semibold text-white text-sm transition-all duration-200 flex items-center justify-center gap-2.5 active:scale-[0.98] ${
                                    isAlreadyDone ? 'bg-slate-300 cursor-not-allowed' :
                                    isApprove
                                    ? (isProcessing ? 'bg-indigo-400' : 'bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 shadow-lg shadow-indigo-200/50')
                                    : (isProcessing ? 'bg-[#9a8d8a]' : 'bg-gradient-to-r from-[#78716c] to-[#8b7572] hover:from-[#6b6560] hover:to-[#7d6966] shadow-lg shadow-stone-300/50')
                                }`}
                            >
                                {isProcessing ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : isAlreadyDone ? 'Already Processed' : (
                                    <>
                                        {isApprove ? (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                                        )}
                                        Confirm {action === 'approve' ? 'Approval' : 'Rejection'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right: Workflow Status */}
                <div className="w-full lg:w-[340px] shrink-0 sticky top-8">
                    <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/80 p-6">
                        <div className="flex justify-between items-center mb-5">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em]">Approval Workflow</p>
                            {loadingPath && <div className="w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>}
                        </div>

                        <div className="space-y-6">
                            {approvalPath.map((step, idx) => {
                                const isDone = step.KSTAT === '9';
                                const isRejected = step.KSTAT === '0';
                                const isDrafter = parseInt(step.DOCU_SER) === 11;
                                const isCurrent = !step.KSTAT && (step.KSABUN === sabun?.trim());

                                return (
                                    <div key={idx} className="flex gap-4 relative">
                                        {idx < approvalPath.length - 1 && (
                                            <div className={`absolute left-[13px] top-8 w-0.5 h-10 ${isDone ? 'bg-emerald-400' : 'bg-slate-200'}`}></div>
                                        )}
                                        <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold z-10 ${
                                            isDone ? 'bg-emerald-500 text-white ring-4 ring-emerald-50' :
                                            isRejected ? 'bg-[#8b7572] text-white ring-4 ring-stone-50' :
                                            isCurrent ? 'bg-indigo-500 text-white ring-4 ring-indigo-50 animate-pulse' :
                                            'bg-slate-100 text-slate-400'
                                        }`}>
                                            {isDone ? (
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                            ) : isRejected ? '!' : idx + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex justify-between items-center gap-2">
                                                <p className={`text-sm font-semibold truncate ${isDone ? 'text-slate-800' : isCurrent ? 'text-indigo-600' : 'text-slate-400'}`}>
                                                    {step.KNAME}
                                                </p>
                                                <span className={`text-[9px] px-2 py-0.5 rounded-md font-medium shrink-0 ${
                                                    isDone ? 'bg-emerald-50 text-emerald-600' :
                                                    isRejected ? 'bg-stone-100 text-[#78655f]' :
                                                    isCurrent ? 'bg-indigo-50 text-indigo-600' :
                                                    'bg-slate-50 text-slate-400'
                                                }`}>
                                                    {isDone ? (isDrafter ? 'Submitted' : 'Approved') : isRejected ? 'Rejected' : isCurrent ? 'Current' : 'Pending'}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{step.pos_nm || (isDrafter ? 'Drafter' : 'Approver')}</p>
                                            {isDone && step.KDATE && (
                                                <p className="text-[9px] text-slate-400 mt-1.5 font-mono">
                                                    {step.KDATE.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} {formatTime(step.KTIME)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ApprovalActionPage;
